// Pure helpers for the Issue Analytics page (src/pages/admin/IssueAnalytics.js).
//
// The Platform returns one cell per (website, agent family, scan title[, ISO week]).
// Scan titles are free-form — the team pushes scans from staging to production under
// names like "B&H Photo Product Info — 500 PDPs — 2026-07-13" — so the page groups by
// the agent family the Platform resolves (`agent_family_key`, e.g. "product-info") by
// default and keeps the scan title (`agent_key`) as the drill-down variant. Weekly rows
// (`bucket_start`) are summed here for every total, and read as-is for the trend. Older
// Platform builds without the family fields fall back to the scan title transparently.
//
// Everything here is a pure function over the fetched rows so it can be unit tested
// (src/pages/admin/__tests__/issueAnalyticsHelpers.test.js); the page only wires state.

import { classifyDiscardReason } from "./issueAnalytics/discardReasons";
import { MIN_ISSUES_PER_POINT, wilsonInterval } from "./issueAnalytics/stats";

export const AGENT_GROUPINGS = [
  { id: "family", label: "Agent type" },
  { id: "title", label: "Scan title" },
];

export const UNKNOWN_AGENT = "(unknown)";
export const ALL_AGENTS_KEY = "__all__";

export function agentGroupKey(row, grouping = "family") {
  if (!row) return UNKNOWN_AGENT;
  if (grouping === "family") {
    return row.agent_family_key || row.agent_key || UNKNOWN_AGENT;
  }
  return row.agent_key || UNKNOWN_AGENT;
}

export function agentGroupTitle(row, grouping = "family") {
  if (!row) return UNKNOWN_AGENT;
  if (grouping === "family") {
    return row.agent_family_title || row.agent_title || row.agent_key || UNKNOWN_AGENT;
  }
  return row.agent_title || row.agent_key || UNKNOWN_AGENT;
}

export function variantKey(row) {
  return (row && row.agent_key) || UNKNOWN_AGENT;
}

export function variantTitle(row) {
  return (row && (row.agent_title || row.agent_key)) || UNKNOWN_AGENT;
}

// Group key -> display title, from every row we have (cells first, then discards).
export function buildAgentTitleMap(cells, discards, grouping = "family") {
  const m = {};
  for (const c of cells || []) {
    const k = agentGroupKey(c, grouping);
    if (!m[k]) m[k] = agentGroupTitle(c, grouping);
  }
  for (const d of discards || []) {
    const k = agentGroupKey(d, grouping);
    if (!m[k]) m[k] = agentGroupTitle(d, grouping);
  }
  return m;
}

// Does a cell / discard row fall inside the current selection?
// `selection` = { agent, variant, client } (any may be empty).
export function matchesSelection(row, selection = {}, grouping = "family") {
  if (selection.agent && agentGroupKey(row, grouping) !== selection.agent) return false;
  if (selection.variant && variantKey(row) !== selection.variant) return false;
  if (selection.client && row.website_id !== selection.client) return false;
  return true;
}

function rateOrNull(numer, denom) {
  return denom > 0 ? numer / denom : null;
}

function withRates(acc) {
  const { low, high } = wilsonInterval(acc.discarded, acc.total);
  return {
    ...acc,
    reviewed: acc.closed + acc.discarded,
    // Reject rate uses TOTAL as denominator (see the page's KPI caveat); the Wilson
    // bounds drive "worst / best first" so tiny samples do not top the lists.
    rejectRate: rateOrNull(acc.discarded, acc.total),
    rejectLow: low,
    rejectHigh: high,
    confirmedDoneRate: rateOrNull(acc.closed, acc.total),
  };
}

function addCounts(acc, c) {
  acc.total += c.total || 0;
  acc.open += c.open_count || 0;
  acc.closed += c.closed_count || 0;
  acc.discarded += c.discarded_count || 0;
}

// Headline totals honoring the selection.
export function buildTotals(cells, selection = {}, grouping = "family") {
  const acc = { total: 0, open: 0, closed: 0, discarded: 0 };
  for (const c of cells || []) {
    if (!matchesSelection(c, selection, grouping)) continue;
    addCounts(acc, c);
  }
  return withRates(acc);
}

// Per-agent rollup honoring the client selection. Each row carries its `variants`
// (distinct scan titles inside the group, most issues first) with their own rates.
export function buildAgentStats(cells, { grouping = "family", selectedWebsite = null } = {}) {
  const acc = {};
  for (const c of cells || []) {
    if (selectedWebsite && c.website_id !== selectedWebsite) continue;
    const key = agentGroupKey(c, grouping);
    if (!acc[key]) {
      acc[key] = {
        key,
        title: agentGroupTitle(c, grouping),
        total: 0,
        open: 0,
        closed: 0,
        discarded: 0,
        websites: new Set(),
        variantMap: {},
      };
    }
    const a = acc[key];
    addCounts(a, c);
    a.websites.add(c.website_id);
    const vk = variantKey(c);
    if (!a.variantMap[vk]) {
      a.variantMap[vk] = { key: vk, title: variantTitle(c), total: 0, open: 0, closed: 0, discarded: 0, websites: new Set() };
    }
    addCounts(a.variantMap[vk], c);
    a.variantMap[vk].websites.add(c.website_id);
  }
  return Object.values(acc).map((a) => {
    const { websites, variantMap, ...rest } = a;
    const variants = Object.values(variantMap)
      .map(({ websites: vw, ...v }) => ({ ...withRates(v), websiteCount: vw.size }))
      .sort((x, y) => y.total - x.total || x.title.localeCompare(y.title));
    return { ...withRates(rest), websiteCount: websites.size, variants, variantCount: variants.length };
  });
}

// Per-client rollup honoring the agent / variant selection.
export function buildClientStats(cells, { grouping = "family", selectedAgent = null, selectedVariant = null, websiteById = {} } = {}) {
  const acc = {};
  for (const c of cells || []) {
    if (!matchesSelection(c, { agent: selectedAgent, variant: selectedVariant }, grouping)) continue;
    const wid = c.website_id;
    if (!acc[wid]) {
      acc[wid] = { key: wid, website_id: wid, total: 0, open: 0, closed: 0, discarded: 0, agents: new Set() };
    }
    addCounts(acc[wid], c);
    acc[wid].agents.add(agentGroupKey(c, grouping));
  }
  return Object.values(acc).map(({ agents, ...w }) => ({
    ...withRates(w),
    agentCount: agents.size,
    name: (websiteById[w.website_id] && websiteById[w.website_id].website_name) || w.website_id,
  }));
}

// Top-level lists hide entities without any explicit signal (a close or a discard)
// unless the other axis is selected, and always hide entities under the volume floor.
export function applyListFilters(rows, { minVolume = 0, requireSignal = true } = {}) {
  let kept = rows || [];
  if (requireSignal) kept = kept.filter((r) => r.closed + r.discarded > 0);
  const beforeFloor = kept.length;
  kept = kept.filter((r) => r.total >= (minVolume || 0));
  return { rows: kept, belowFloor: beforeFloor - kept.length };
}

// Rank-list ordering. "Worst first" sorts by the Wilson lower bound, "best first" by
// the upper bound, so small samples sink in both directions.
export function sortRows(rows, sortId, getName) {
  const cmpNullable = (a, b, key, dir) => {
    if (a[key] == null && b[key] == null) return b.total - a.total;
    if (a[key] == null) return 1;
    if (b[key] == null) return -1;
    return dir === "asc" ? a[key] - b[key] : b[key] - a[key];
  };
  const cmp = (a, b) => {
    switch (sortId) {
      case "reject_asc":
        return cmpNullable(a, b, "rejectHigh", "asc");
      case "reject_desc":
        return cmpNullable(a, b, "rejectLow", "desc") || b.total - a.total;
      case "done_desc":
        return cmpNullable(a, b, "confirmedDoneRate", "desc");
      case "volume_desc":
        return b.total - a.total;
      case "name_asc":
        return getName(a).localeCompare(getName(b));
      default:
        return 0;
    }
  };
  return [...(rows || [])].sort(cmp);
}

// Agent × client matrix (each cell sums every scan title / week of the group) plus
// the per-axis overall rates.
export function buildMatrix(cells, grouping = "family") {
  const matrix = {};
  const aTotals = {};
  const wTotals = {};
  for (const c of cells || []) {
    const a = agentGroupKey(c, grouping);
    const w = c.website_id;
    matrix[a] = matrix[a] || {};
    const cell = (matrix[a][w] = matrix[a][w] || { total: 0, open_count: 0, closed_count: 0, discarded_count: 0 });
    cell.total += c.total || 0;
    cell.open_count += c.open_count || 0;
    cell.closed_count += c.closed_count || 0;
    cell.discarded_count += c.discarded_count || 0;
    aTotals[a] = aTotals[a] || { total: 0, open: 0, closed: 0, discarded: 0 };
    wTotals[w] = wTotals[w] || { total: 0, open: 0, closed: 0, discarded: 0 };
    addCounts(aTotals[a], c);
    addCounts(wTotals[w], c);
  }
  const overall = (t) => Object.fromEntries(Object.entries(t).map(([k, v]) => [k, withRates(v)]));
  return { matrix, agentOverall: overall(aTotals), clientOverall: overall(wTotals) };
}

// Heatmap axes: the worst `topN` entities on each axis by Wilson lower bound, among
// those with an explicit signal and at least `minVolume` issues.
export function rankHeatmapAxes({ agentOverall, clientOverall }, { minVolume = 0, topN = 12 } = {}) {
  const pick = (overall) => {
    const eligible = Object.entries(overall).filter(([, v]) => v.closed + v.discarded > 0 && v.total >= (minVolume || 0));
    const sorted = eligible.sort(([, a], [, b]) => (b.rejectLow || 0) - (a.rejectLow || 0) || b.total - a.total);
    const keep = sorted.slice(0, topN).map(([k]) => k);
    return { keep, hidden: Math.max(0, sorted.length - keep.length) };
  };
  const agents = pick(agentOverall || {});
  const clients = pick(clientOverall || {});
  return { agentKeys: agents.keep, websiteKeys: clients.keep, hiddenAgents: agents.hidden, hiddenClients: clients.hidden };
}

// Tag every discard with its reason category once.
export function categorizeDiscards(discards) {
  return (discards || []).map((d) => ({ ...d, reason_category: classifyDiscardReason(d.discard_reason) }));
}

// Discard list honoring the selection, the reason category and a free-text search.
export function filterDiscards(discards, { grouping = "family", agent = "", variant = "", client = "", reason = "", search = "" } = {}) {
  let list = discards || [];
  if (agent || variant || client) {
    list = list.filter((d) => matchesSelection(d, { agent, variant, client }, grouping));
  }
  if (reason) {
    list = list.filter((d) => (d.reason_category || classifyDiscardReason(d.discard_reason)) === reason);
  }
  if (search) {
    const term = search.toLowerCase();
    list = list.filter((d) =>
      [d.discard_reason, d.issue_title, d.discarded_by, d.website_id, d.agent_title, d.agent_family_title]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(term))
    );
  }
  return list;
}

// Weekly reject-rate series from bucketed cells. With an agent selected the chart
// follows that agent (and client, if selected); otherwise the busiest `maxSeries`
// groups in the slice plus an "All agents" line. Weeks with fewer than
// `minIssuesPerPoint` issues yield a null (a gap) instead of a noisy rate.
export function buildWeeklyTrend(
  cells,
  { grouping = "family", agent = "", variant = "", client = "", maxSeries = 5, minIssuesPerPoint = MIN_ISSUES_PER_POINT } = {}
) {
  const scoped = (cells || []).filter((c) => c.bucket_start && matchesSelection(c, { agent, variant, client }, grouping));
  if (scoped.length === 0) return { weeks: [], series: [], data: [] };

  const weeks = Array.from(new Set(scoped.map((c) => c.bucket_start))).sort();
  const byGroup = {};
  const all = { key: ALL_AGENTS_KEY, title: "All agents", total: 0, points: {} };
  const bump = (series, c) => {
    const p = (series.points[c.bucket_start] = series.points[c.bucket_start] || { total: 0, discarded: 0 });
    p.total += c.total || 0;
    p.discarded += c.discarded_count || 0;
    series.total += c.total || 0;
  };
  for (const c of scoped) {
    const key = agentGroupKey(c, grouping);
    byGroup[key] = byGroup[key] || { key, title: agentGroupTitle(c, grouping), total: 0, points: {} };
    bump(byGroup[key], c);
    bump(all, c);
  }

  let series;
  if (agent) {
    series = [byGroup[agent]].filter(Boolean);
  } else {
    series = Object.values(byGroup)
      .sort((a, b) => b.total - a.total)
      .slice(0, maxSeries);
    if (Object.keys(byGroup).length > 1) series.push(all);
  }
  const finish = (s) => ({
    ...s,
    points: Object.fromEntries(
      weeks.map((w) => {
        const p = s.points[w] || { total: 0, discarded: 0 };
        return [w, { ...p, rate: p.total >= minIssuesPerPoint ? p.discarded / p.total : null }];
      })
    ),
  });
  const finished = series.map(finish);
  const data = weeks.map((w) => {
    const row = { week: w };
    for (const s of finished) {
      row[s.key] = s.points[w].rate;
      row[`${s.key}__n`] = s.points[w].total;
    }
    return row;
  });
  return { weeks, series: finished, data };
}

// One-line summary of the scan titles behind a family row, for tooltips.
export function describeVariants(variants, max = 8) {
  if (!variants || variants.length === 0) return "";
  const shown = variants.slice(0, max).map((v) => `${v.title} (${v.total.toLocaleString()})`);
  const rest = variants.length - shown.length;
  return shown.join("\n") + (rest > 0 ? `\n… ${rest} more` : "");
}
